import { Reveal, RevealStagger, StaggerItem } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";
import { FinalCTA } from "../components/FinalCTA";
import { FloatingMotif } from "../components/FloatingMotif";
import { CredibilityStrip } from "../components/CredibilityStrip";
import { RevealStat } from "../components/Counter";
import { motion } from "framer-motion";
import { WHY } from "../data/site";

const EASE = [0.16, 1, 0.3, 1];

// Editorial signature panel — instead of stock portrait.
const SignaturePanel = () => (
    <div className="relative w-full aspect-[4/5] bg-[#0f0f0f] text-white overflow-hidden">
        <div
            className="absolute inset-0 opacity-[0.18] pointer-events-none"
            style={{
                backgroundImage:
                    "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
                backgroundSize: "36px 36px",
            }}
        />
        <div className="absolute top-6 left-6 right-6 flex items-start justify-between text-[10px] tracking-[0.28em] uppercase text-white/60">
            <span>Folio · 02</span>
            <span>About / Manifesto</span>
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
            <p className="font-serif italic text-2xl md:text-3xl text-white/70">
                We don’t make
            </p>
            <p className="font-serif text-5xl md:text-7xl leading-none mt-2">
                websites.
            </p>
            <span className="my-6 h-px w-16 bg-white/30 inline-block" />
            <p className="font-serif italic text-2xl md:text-3xl text-white/70">
                We build
            </p>
            <p className="font-serif text-5xl md:text-7xl leading-none mt-2">
                presence.
            </p>
        </div>
        <svg
            className="absolute bottom-6 left-6 right-6 h-12"
            viewBox="0 0 400 40"
            preserveAspectRatio="none"
            aria-hidden
        >
            <motion.path
                d="M 0 30 C 80 8, 160 36, 240 18 S 360 4, 400 22"
                stroke="white"
                strokeWidth="1"
                fill="none"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.8, ease: EASE }}
            />
        </svg>
        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-[10px] tracking-[0.28em] uppercase text-white/60 pt-10">
            <span>Pixemance</span>
            <span>Signed · MMXXVI</span>
        </div>
    </div>
);

export default function About() {
    return (
        <div data-testid="page-about">
            {/* Header */}
            <section className="relative pt-12 md:pt-20 pb-20 md:pb-28 overflow-hidden">
                <FloatingMotif />
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
                        <div className="md:col-span-7">
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8">
                                    <span className="h-px w-10 bg-[#111111]" />
                                    <span className="text-[10px] tracking-[0.28em] uppercase font-medium">
                                        About · Pixemance
                                    </span>
                                </div>
                            </Reveal>
                            <Reveal delay={0.05}>
                                <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[0.98] tracking-tight">
                                    A firm built
                                    <br />
                                    <em>for serious business.</em>
                                </h1>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p className="mt-10 text-lg md:text-xl text-[#444444] max-w-2xl leading-relaxed">
                                    Pixemance is a business-first digital
                                    presence and performance firm. We work
                                    with a small number of serious businesses
                                    each year — and treat each one the way a
                                    senior consulting partner would: with
                                    restraint, clarity, and a long view.
                                </p>
                            </Reveal>
                            <Reveal delay={0.25}>
                                <div className="mt-12 flex flex-wrap gap-4">
                                    <MagneticButton to="/contact" testid="about-cta-primary">
                                        Start a conversation
                                    </MagneticButton>
                                    <MagneticButton to="/work" testid="about-cta-secondary" variant="outline">
                                        See selected work
                                    </MagneticButton>
                                </div>
                            </Reveal>
                        </div>
                        <div className="md:col-span-5">
                            <Reveal delay={0.1}>
                                <SignaturePanel />
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <CredibilityStrip />

            {/* Philosophy */}
            <section className="bg-[#111111] text-white py-24 md:py-36 relative overflow-hidden">
                <FloatingMotif variant="dark" />
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-2">
                            <p className="text-[10px] tracking-[0.28em] uppercase text-white/60">
                                § Philosophy
                            </p>
                        </div>
                        <div className="md:col-span-10">
                            <Reveal>
                                <p className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.12] max-w-5xl">
                                    Most agencies start with a screen. We
                                    start with the business behind it.{" "}
                                    <em className="text-white/60">
                                        The website is the last decision —
                                        not the first.
                                    </em>{" "}
                                    Get the strategy right, and the design
                                    becomes obvious.
                                </p>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <div className="mt-12 flex items-center gap-4 text-[10px] tracking-[0.28em] uppercase text-white/60">
                                    <span className="h-px w-12 bg-white/50 inline-block" />
                                    Pixemance · Founding principle
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* Principles */}
            <section className="py-24 md:py-36">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
                        <div className="md:col-span-4">
                            <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                                § Principles
                            </p>
                            <Reveal>
                                <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-tight">
                                    Five things
                                    <br />
                                    <em>we believe.</em>
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <RevealStagger className="border-t border-[#111111]">
                        {WHY.map((w, i) => (
                            <StaggerItem key={w.title}>
                                <div className="grid grid-cols-12 gap-6 py-8 md:py-12 border-b border-[#dddddd] group hover:bg-[#fafafa] transition-colors duration-500">
                                    <span className="col-span-2 md:col-span-1 font-serif italic text-3xl text-[#444444]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <h3 className="col-span-10 md:col-span-5 font-serif text-2xl md:text-3xl text-[#111111] leading-tight">
                                        {w.title}
                                    </h3>
                                    <p className="col-span-12 md:col-span-6 text-[#444444] text-lg leading-relaxed">
                                        {w.body}
                                    </p>
                                </div>
                            </StaggerItem>
                        ))}
                    </RevealStagger>
                </div>
            </section>

            {/* Stats with animated counters */}
            <section className="bg-[#fafafa] border-y border-[#dddddd] py-16 md:py-20">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444] mb-8">
                        § By the numbers
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[#dddddd]">
                        <RevealStat value={12} suffix="+" label="Industries served" />
                        <RevealStat value={94} suffix="%" label="Client retention" />
                        <RevealStat value={437} suffix="%" label="Top enquiry uplift" />
                        <RevealStat value={4.9} decimals={1} suffix="★" label="Average review score" />
                    </div>
                </div>
            </section>

            <FinalCTA />
        </div>
    );
}
